import React, { FunctionComponent, useEffect, useMemo } from 'react';
import { useTheme } from '@mui/material/styles';

import BrunchDiningIcon from '@mui/icons-material/BrunchDining';

import {
  Avatar,
  Box,
  Button
} from '@mui/material';

import FileUploadIcon from '@mui/icons-material/FileUpload';

import {
  Typography
} from '../styled';

type Props = {
  sx?: object;
  caption?: string;
  fileInputName?: string;
  onChange?: (e:File|FileList|undefined|null) => void;
  selected?: File|FileList|undefined|null;
  multi?: boolean;
  food?: boolean;
  fallback?: string;
  accept?: string;
};

export const FileUpload: FunctionComponent<Props> = ({sx, onChange, caption, fileInputName, multi, selected, food, fallback, accept}) => {
  const theme = useTheme();

  // Controlled component: `selected` is the source of truth (a single File, or a
  // FileList when `multi`). We intentionally do NOT keep separate internal state and
  // echo it back through `onChange`. That echo formed a render loop
  // (input -> setFile -> onChange -> parent setState -> new `selected` -> setFile -> ...)
  // which, combined with a fresh URL.createObjectURL on every render, made the preview
  // flicker after a pick. `onChange` now only fires from the input handler.
  const file = !multi ? (selected as File | undefined) : undefined;
  const files = !!multi ? (selected as FileList | undefined | null) : undefined;

  const previewFile = !!multi ? files?.[0] : file;

  // One stable object URL per picked file, revoked when the file changes or on unmount,
  // so the browser doesn't reload the image every render. `fallback` is a backend URL
  // we don't own and must never be revoked.
  const objectUrl = useMemo(() => {
    if (!previewFile) return undefined;
    return URL.createObjectURL(previewFile);
  }, [previewFile]);

  useEffect(() => {
    if (!objectUrl) return;
    return () => { URL.revokeObjectURL(objectUrl); };
  }, [objectUrl]);

  const avatarSrc = objectUrl ?? fallback;

  return (<>
    {!!caption && <Typography variant="caption">{caption}</Typography>}
    <Box sx={{
      padding: 1,
      width: "100%",
      borderRadius: 18,
      backgroundColor: 'rgba(0, 0, 0, 0.05)',
      cursor: 'pointer',
      display: 'flex',
      flexDirection: 'row',
      overflow: 'hidden'
    }} onClick={() => {
      // @ts-ignore
      if (!!document.getElementById(!!fileInputName ? fileInputName : "fileInput")) document.getElementById(!!fileInputName ? fileInputName : "fileInput").click();
    }}>
      <Avatar sx={{
        marginRight: 1
      }} src={avatarSrc}>
        {!!food && <BrunchDiningIcon />}
      </Avatar>
      {!!multi ? ((files || []).length < 1) : (!file) && <Button variant="text" endIcon={<FileUploadIcon />}>
        Upload
      </Button>}
      {!!multi && !!files && (files || []).length > 0 && <Typography variant="caption" sx={{
        marginTop: 'auto',
        marginBottom: 'auto'
      }}>
        {Array.from(files).map(ff => ff.name).join(", ")}
      </Typography>}
      {!multi && !!file && <Typography variant="caption" sx={{
        marginTop: 'auto',
        marginBottom: 'auto'
      }}>
        {file.name}
      </Typography>}
    </Box>


    <input type="file" style={{display: 'none'}} id={!!fileInputName ? fileInputName : "fileInput"} onChange={(e) => {
      if (!multi) {
        if (!!onChange) onChange((e?.currentTarget?.files || [])[0]);
      }
      if (!!multi) {
        if (!!onChange) onChange(e?.currentTarget?.files ?? null);
      }
    }} accept={!!accept ? accept : "image/jpeg, image/png, image/jpg, image/gif, video/*, audio/*, text/*, application/*"} />
  </>);
}
